import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import TeesSubNav from '../components/TeesSubNav';
import TeesProductCard from '../components/TeesProductCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category labels
const CATEGORY_LABELS = {
  'short-sleeve': 'Short Sleeve',
  'long-sleeve': 'Long Sleeve',
  'new-designs': 'New Designs',
  'caps': 'Headwear',
  'hoodies': 'Hoodies',
  'tank-tops': 'Tank Tops'
};

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('tees') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function TeesCategory() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { itemCount, openCart } = useCart();

  const categoryLabel = CATEGORY_LABELS[category] || category;

  useEffect(() => {
    fetchProducts();
  }, [category]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tees/category/${category}`);
      setProducts(response.data.filter(p => p.active));
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      <TeesSubNav />

      {/* Breadcrumb / Back Link */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px 20px 0'
      }}>
        <Link 
          to="/shop/tees" 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: '#666',
            textDecoration: 'none',
            fontSize: '14px',
            transition: 'color 0.2s'
          }}
        >
          <ArrowLeft size={18} />
          Back to Categories
        </Link>
      </div>

      {/* Category Title */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px 20px 30px'
      }}>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '36px',
          margin: '0 0 10px',
          color: '#1a1a1a'
        }}>
          {categoryLabel}
        </h1>
        <p style={{
          color: '#666',
          fontSize: '16px',
          margin: 0
        }}>
          {products.length > 0 
            ? `${products.length} design${products.length !== 1 ? 's' : ''} available`
            : 'Browse our collection'
          }
        </p>
      </div>

      {/* Main Content - Product Grid */}
      <main style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px 20px 60px' 
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="loading-spinner" />
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
              No designs in this category yet. Check back soon!
            </p>
            <Link to="/shop/tees">
              <Button variant="outline">
                <ArrowLeft size={18} style={{ marginRight: '8px' }} />
                Browse Other Categories
              </Button>
            </Link>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {products.map(product => (
              <TeesProductCard key={product.id} product={product} category={category} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

export default TeesCategory;
